import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Project } from '../objects/project';
import { User } from '../objects/user';
import { Axis, CargoSpace, Load, LoadingSpaceType } from '../types';
import { CargoSpacesSummaryService } from './cargo-spaces-summary.service';
import { ColorsService } from './colors.service';
import { ProjectApiService } from './project-api.service';
import { ProjectService } from './project.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalsService {
  public window;
  private currentWorkSpace: number;
  private user: User;
  private project: Project;

  constructor(
    private projectService: ProjectService,
    private projectApiService: ProjectApiService,
    private userService: UserService,
    private translate: TranslateService,
    private cargoSpacesSummary: CargoSpacesSummaryService,
    private colorsService: ColorsService,
  ) {
    this.projectService.currentWorkSpaceIndex.subscribe(
      (current) => (this.currentWorkSpace = current),
    );
  }

  public init() {
    window.Api = {};
    this.window = window.Api;
    this.userService.user.subscribe((user) => {
      this.user = user;
      this.accountInfo();
    });
    this.projectService.project.subscribe((project) => {
      this.project = project;
      this.loadingSpaceInfo();
      this.loadsInfo();
      this.createAddLoadingSpace();
      this.createAddLoad();
      this.createUpdateLoadingSpace();
      this.createUpdateGroupLoad();
    });
  }

  private loadingSpaceInfo() {
    this.window.loadingSpace = [];

    this.project.workSpaces[this.currentWorkSpace]?.cargoSpaces.forEach((cargo: CargoSpace, index) => {
      const summary = this.cargoSpacesSummary.calculateCargoSpacesSummary(index);
      this.window.loadingSpace.push({
        id: cargo.id,
        name: this.translate.instant(cargo.name),
        dimensions: cargo.dimensions,
        loadCapacity: cargo.limit,
        freeLdm: summary[index]?.freeLdm,
        takenLdm: summary[index]?.takenLdm,
        loadedLoads: summary[index]?.loadedLoads,
        notLoadedLoads: summary[index]?.notLoadedLoads,
        occupiedVolume: summary[index]?.occupiedVolume,
        freeVolume: summary[index]?.freeVolume,
        totalWeight: summary[index]?.totalWeight,
        freeArea: summary[index]?.freeArea,
        axi: cargo.axis
      });
    });
  }

  private loadsInfo() {
    this.window.loads = {
      cargoSpaces: [],
      outsideSpace: [],
    };

    this.project.workSpaces[this.currentWorkSpace]?.cargoSpaces[0].groups.forEach((loadGroup) => {
      this.window.loads.cargoSpaces.push({
        id: loadGroup.id,
        name: this.translate.instant(loadGroup.name),
        dimensions: loadGroup.dimensions,
        quantity: loadGroup.quantity,
        weigth: loadGroup.weight,
        totalWeigth: loadGroup.weight * loadGroup.quantity,
        stackingOption: loadGroup.stacking,
      });
    });

    this.project.workSpaces[this.currentWorkSpace]?.cargoSpaces[1]?.groups.forEach((loadGroup) => {
      this.window.loads.cargoSpaces.push({
        id: loadGroup.id,
        name: this.translate.instant(loadGroup.name),
        dimensions: loadGroup.dimensions,
        quantity: loadGroup.quantity,
        weight: loadGroup.weight,
        totalWeight: loadGroup.weight * loadGroup.quantity,
        stackingOption: loadGroup.stacking,
      });
    });

    this.project.workSpaces[this.currentWorkSpace]?.outsideSpace.groups.forEach((loadGroup) => {
      this.window.loads.outsideSpace.push({
        id: loadGroup.id,
        name: this.translate.instant(loadGroup.name),
        dimensions: loadGroup.dimensions,
        quantity: loadGroup.quantity,
        weigth: loadGroup.weight,
        totalWeigth: loadGroup.weight * loadGroup.quantity,
        stackingOption: loadGroup.stacking,
      });
    });
  }

  private accountInfo() {
    this.window.account = {
      email: this.user.email,
      role: this.user.role,
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      companyName: this.user.companyName,
      trucks: this.user.trucks,
      loads: this.user.loads,
      savedProjects: this.user.savedProjects,
      country: this.user.country,
      company: this.user.company
        ? {
            country: this.user.company.country,
            legalName: this.user.company.legalName,
            locality: this.user.company.locality,
            postalCode: this.user.company.postalCode,
            streetAddress: this.user.company.streetAddress,
            vatId: this.user.company.vatId,
          }
        : undefined,
      isPremium: this.user.isPremium,
    };
  }

  private createAddLoadingSpace() {
    this.window.addLoadingSpace = (
      name: string,
      type: number,
      quantity: number,
      length: number,
      width: number,
      height: number,
      limit: number,
      set: boolean,
      length1?: number,
      width1?: number,
      height1?: number,
      limit1?: number,
      axis?: Axis[],
      id?: number,
    ) => {
      const loadingSpace: Partial<LoadingSpaceType> = {
        name,
        length,
        width,
        height,
        limit,
        set,
        length1: set ? length1 : undefined,
        width1: set ? width1 : undefined,
        height1: set ? height1 : undefined,
        limit1: set ? limit1 : 0,
        type,
        axis: axis ? axis : undefined,
      };

      const loadingSpaceAdd = {
        currentWorkSpaceId: 1,
        newLoadingSpaces: {
          quantity: quantity,
          loadingSpace: {
            ...loadingSpace,
            id: id ? id : null,
          },
        },
      };
      this.projectApiService.addLoadingSpace(loadingSpaceAdd).subscribe();
    };
  }

  private createAddLoad() {
    this.window.addLoad = (
      name: string,
      length: number,
      width: number,
      height: number,
      weight: number,
      quantity: number,
      stacking: boolean,
      allowToRotate: boolean,
      maxLoadsOnTop?: number,
      toFirstEmpty?: boolean,
    ) => {
      const addLoad: Partial<Load> = {
        name,
        length,
        width,
        height,
        weight,
        stacking,
        allowToRotate,
      };

      if (stacking && maxLoadsOnTop) {
        addLoad.stackingOptions = { maxLoadsOnTop };
      }

      addLoad.color = this.colorsService.getColor();
      const currentWorkSpaceId = this.project.workSpaces[this.currentWorkSpace]
        ? this.project.workSpaces[this.currentWorkSpace].id
        : undefined;
      const addGroup = {
        currentWorkSpaceId: currentWorkSpaceId,
        newLoads: [
          {
            quantity,
            load: addLoad,
          },
        ],
        workSpaceId: toFirstEmpty ? undefined : currentWorkSpaceId,
      };

      this.projectApiService.addGroup(addGroup).subscribe();
    };
  }

  private createUpdateLoadingSpace() {
    this.window.updateLoadingSpace = (
      name: string,
      type: number,
      length: number,
      width: number,
      height: number,
      limit: number,
      set: boolean,
      length1?: number,
      width1?: number,
      height1?: number,
      limit1?: number,
      id?: number,
    ) => {
      const loadingSpace: Partial<LoadingSpaceType> = {
        id: id ? id : null,
        name,
        type,
        length,
        width,
        height,
        limit,
        set,
        length1: set ? length1 : undefined,
        width1: set ? width1 : undefined,
        height1: set ? height1 : undefined,
        limit1: set ? limit1 : undefined,
      };
      const currentWorkSpaceId = this.project.workSpaces[this.currentWorkSpace]
        ? this.project.workSpaces[this.currentWorkSpace].id
        : undefined;
      const loadingSpaceAdd = {
        currentWorkSpaceId: currentWorkSpaceId,
        loadingSpace,
        target: {
          workSpaceId: currentWorkSpaceId,
        },
      };

      this.projectApiService.updateLoadingSpace(loadingSpaceAdd).subscribe();
    };
  }

  private createUpdateGroupLoad() {
    this.window.updateGroupLoad = (
      id: number,
      cargoSpaceId: number,
      name?: string,
      length?: number,
      width?: number,
      height?: number,
      weight?: number,
      quantity?: number,
      stacking?: boolean,
    ) => {
      const groups = [];
      this.project.workSpaces.forEach((cargo) => {
        groups.push(cargo.cargoSpaces[0].groups);
        groups.push(cargo.cargoSpaces[1]?.groups);
        groups.push(cargo.outsideSpace?.groups);
      });
      const group = groups.find((loadGroup) => loadGroup.id === id);
      const load: Partial<Load> = {};
      if (length) {
        load.length = length;
      }
      if (width) {
        load.width = width;
      }
      if (height) {
        load.height = height;
      }
      if (weight) {
        load.weight = weight;
      }
      if (name) {
        load.name = name;
      }
      if (stacking) {
        load.stacking = stacking;
      }

      const workSpace = this.project.workSpaces.find(
        (space) =>
          space.cargoSpaces[0].id === cargoSpaceId || space.cargoSpaces[1].id === cargoSpaceId,
      );
      const editLoad = {
        currentWorkSpaceId: this.project.workSpaces[this.currentWorkSpace].id,
        target: {
          workSpaceId: workSpace.id,
          cargoSpaceId,
          groupId: group.id,
        },
        quantityDifference: quantity ? quantity - group.quantity : 0,
        load,
      };

      this.projectApiService.editGroup(editLoad).subscribe();
    };
  }
}

declare global {
  interface Window {
    Api: any;
  }
}
